import { windowStateAt, type ResolvedWindow } from './racePlan.ts'
import type { LapDoc } from './timing'

/**
 * Is the pit window open, how long for, and how many more laps can this car
 * do before it misses it.
 *
 * Only for a plan with fixed windows. Two states, open and closed, and the
 * headline is always a time: closes in, or opens in. Underneath it, laps,
 * because a car can only pit as it passes the entry, and that is the number a
 * pit wall acts on.
 *
 * The lap arithmetic runs from the last time the car crossed the line, at the
 * car's FASTEST known pace, deliberately: "cannot make it" and "stop next time
 * round" are then certainties rather than estimates. If the quickest lap the
 * car has done will not get it there, nothing will. The one pessimistic bound
 * is the yellow: a lap neutralised at 60 km/h, which is what a full-course
 * yellow thrown as the car passes the entry would cost it.
 *
 * Pace is the fastest in-lap where the car has one, because what matters is
 * reaching the pit entry, not the control line, and an in-lap ends at the line
 * in the pit lane a few seconds past the entry. Without one, the fastest lap.
 */

export type PitWindowPhase = 'noWindows' | 'open' | 'closed' | 'finished'

export type PitWindowUrgency =
  /** A stop has already been made in this window. Nothing to do. */
  | 'stopped'
  /** More than one pass left at fastest pace. */
  | 'within'
  /** The pass after next lands after the close. Stop next time round. */
  | 'nextTimeRound'
  /** A neutralised lap from here lands after the close. Pit now. */
  | 'yellowRisk'
  /** Even the fastest lap lands after the close. */
  | 'cannotMake'
  /** No lap time yet, or the race is stopped. */
  | 'unknown'

export interface PitWindowInput {
  windows: readonly ResolvedWindow[]
  /** The session's clock, not the viewer's. */
  now: Date
  /** When the car last crossed the line. */
  lastCrossing: Date | null
  /** Fastest in-lap, else fastest lap, in milliseconds. */
  paceMs: number | null
  /** What a lap costs under a full-course yellow, if known. */
  yellowLapMs: number | null
  /** The track is neutralised now, so the lap in progress is a yellow lap. */
  underYellow: boolean
  /** Red flag: no laps are being completed. */
  raceStopped: boolean
  /** A pit stop already made in the open window, or null. */
  stoppedAt: Date | null
}

export interface PitWindowAdvice {
  phase: PitWindowPhase
  /** The open window, or the next to open. Null when none remain. */
  window: ResolvedWindow | null
  /** Milliseconds until it closes (open) or opens (closed). */
  remainingMs: number | null
  urgency: PitWindowUrgency | null
  /** Passes of the pit entry left before the close, at fastest pace. */
  passes: number | null
  /** When the car next reaches the line, at the pace assumed. */
  nextPass: Date | null
  /** Closed: whether the next pass falls inside the window once it opens. */
  openNextTimeRound: boolean | null
  /** Closed: how many passes until one falls inside the window. */
  passesUntilOpen: number | null
  /** One line, already phrased. */
  detail: string
  index: number | null
  total: number
}

const nothing: Omit<PitWindowAdvice, 'phase' | 'detail' | 'total'> = {
  window: null,
  remainingMs: null,
  urgency: null,
  passes: null,
  nextPass: null,
  openNextTimeRound: null,
  passesUntilOpen: null,
  index: null,
}

export function pitWindowAdvice(input: PitWindowInput): PitWindowAdvice {
  const { windows, now, lastCrossing, paceMs, yellowLapMs, underYellow, raceStopped, stoppedAt } = input
  if (windows.length === 0) {
    return { ...nothing, phase: 'noWindows', detail: 'No pit windows in the plan.', total: 0 }
  }
  const state = windowStateAt(windows, now)
  const t = now.getTime()
  const pace = paceMs && paceMs > 0 ? paceMs : null
  const yellow = yellowLapMs && yellowLapMs > 0 ? yellowLapMs : null
  // The lap in progress costs a yellow lap if the track is neutralised now.
  const first = underYellow && yellow ? yellow : pace

  if (state.open) {
    const w = state.open
    const close = w.closes.getTime()
    const base = { ...nothing, phase: 'open' as const, window: w, remainingMs: close - t, index: w.index, total: state.total }

    if (stoppedAt) {
      return { ...base, urgency: 'stopped', detail: `Stopped at ${clock(stoppedAt)}.` }
    }
    if (raceStopped) {
      return { ...base, urgency: 'unknown', detail: 'Race stopped.' }
    }
    if (!lastCrossing || !pace || !first) {
      return { ...base, urgency: 'unknown', detail: 'No lap time yet.' }
    }

    const last = lastCrossing.getTime()
    const t1 = last + first
    const nextPass = new Date(t1)
    // At the entry as the window closes is too late; the bound is strict.
    if (t1 >= close) {
      return {
        ...base,
        urgency: 'cannotMake',
        passes: 0,
        nextPass,
        detail: `Cannot make the window. Next pass ${clock(nextPass)}, closes ${clock(w.closes)}.`,
      }
    }
    // Every pass that lands before the close, at fastest pace after the first.
    let passes = 0
    for (let at = t1; at < close; at += pace) passes++
    if (yellow && last + yellow >= close) {
      return {
        ...base,
        urgency: 'yellowRisk',
        passes,
        nextPass,
        detail: 'Pit now. A yellow from here would cost the window.',
      }
    }
    if (passes <= 1) {
      return { ...base, urgency: 'nextTimeRound', passes: 1, nextPass, detail: 'Stop next time round.' }
    }
    return { ...base, urgency: 'within', passes, nextPass, detail: `Stop within ${passes}.` }
  }

  if (state.next) {
    const w = state.next
    const opens = w.opens.getTime()
    const close = w.closes.getTime()
    const base = { ...nothing, phase: 'closed' as const, window: w, remainingMs: opens - t, index: w.index, total: state.total }
    if (raceStopped) return { ...base, detail: 'Race stopped.' }
    if (!lastCrossing || !pace || !first) return { ...base, detail: `Opens ${clock(w.opens)}.` }

    const last = lastCrossing.getTime()
    const t1 = last + first
    const nextPass = new Date(t1)
    // The first pass that lands inside the window, counting from the next.
    let passesUntilOpen = 1
    let at = t1
    while (at < opens && at <= close + pace) {
      at += pace
      passesUntilOpen++
    }
    const inside = at >= opens && at < close
    const openNextTimeRound = t1 >= opens && t1 < close
    return {
      ...base,
      nextPass,
      openNextTimeRound,
      passesUntilOpen: inside ? passesUntilOpen : null,
      detail: openNextTimeRound
        ? 'Open next time round.'
        : inside
          ? `Not open next time round. Open in ${passesUntilOpen} passes.`
          : `Not open next time round.`,
    }
  }

  return { ...nothing, phase: 'finished', detail: 'No more windows.', total: state.total }
}

/**
 * The pace the advice runs on: the car's fastest in-lap in this session, else
 * its fastest lap.
 *
 * An in-lap longer than half as much again as the best lap is not an in-lap
 * the way this wants one; it is a lap flagged by an earlier detector as the
 * lap containing the stop, and is ignored rather than trusted.
 */
export function pitWindowPace(laps: readonly LapDoc[], bestLapMs: number | null | undefined): number | null {
  let best: number | null = bestLapMs && bestLapMs > 0 ? bestLapMs : null
  for (const l of laps) {
    if (l.lapTimeMs && l.lapTimeMs > 0 && (best == null || l.lapTimeMs < best)) best = l.lapTimeMs
  }
  if (best == null) return null
  let inLap: number | null = null
  for (const l of laps) {
    if (!l.inLap || !l.lapTimeMs || l.lapTimeMs <= 0) continue
    if (l.lapTimeMs > best * 1.5) continue
    if (inLap == null || l.lapTimeMs < inLap) inLap = l.lapTimeMs
  }
  return inLap ?? best
}

/**
 * A pit stop already made inside the open window: the latest completed stop
 * or in-lap whose time falls inside it, or the pit lane now.
 */
export function stoppedInWindow(
  window: ResolvedWindow | null,
  now: Date,
  stops: readonly { completedAt?: { toDate(): Date } | null }[],
  laps: readonly LapDoc[],
  inPitsNow: boolean,
): Date | null {
  if (!window) return null
  const from = window.opens.getTime()
  const to = Math.min(window.closes.getTime(), now.getTime())
  let latest: Date | null = null
  const consider = (d: Date | null | undefined) => {
    if (!d) return
    const ms = d.getTime()
    if (ms >= from && ms <= to && (latest == null || ms > latest.getTime())) latest = d
  }
  for (const s of stops) consider(s.completedAt?.toDate())
  for (const l of laps) if (l.inLap) consider(l.completedAt?.toDate())
  if (latest) return latest
  return inPitsNow ? now : null
}

function clock(d: Date): string {
  return d.toISOString().slice(11, 19)
}
